import { FirebaseError } from 'firebase/app';
import { FIREBASE_AUTH_ERROR_MESSAGES } from '@/constants/firebase';
import { ApiFetcherError } from '@/lib/api-fetcher';
import toastPopper from '@/lib/toast-popper';

type ErrorHandler = {
  canHandle: (error: unknown) => boolean;
  handle: (error: unknown) => string;
};

const firebaseErrorHandler: ErrorHandler = {
  canHandle: (error: unknown): error is FirebaseError => error instanceof FirebaseError,
  handle: (error: unknown) => {
    // * 使用 type guard 來確保型別安全
    if (error instanceof FirebaseError) {
      const errorCode = error.code as keyof typeof FIREBASE_AUTH_ERROR_MESSAGES;
      const errorMessage = FIREBASE_AUTH_ERROR_MESSAGES[errorCode] || 'Undefined firebase error';

      toastPopper.error({ description: errorMessage });
      console.error(`Firebase error: ${error.code} ${error.message}`);

      return errorMessage;
    }
    return 'Not firebase error';
  },
};

const apiFetcherErrorHandler: ErrorHandler = {
  canHandle: (error: unknown): error is ApiFetcherError => error instanceof ApiFetcherError,
  handle: (error: unknown) => {
    if (error instanceof ApiFetcherError) {
      toastPopper.error({ description: error.message });
      console.error(`Api fetcher error: ${error.status} ${error.message}`);

      return error.message;
    }
    return 'Not api fetcher error';
  },
};

const generalErrorHandler: ErrorHandler = {
  canHandle: () => true, // * 捕捉所有未被其他 handler 處理的錯誤
  handle: (error: unknown) => {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'An unknown error occurred, no message from the error';

    toastPopper.error({ description: errorMessage });
    console.error(`General error: ${errorMessage}`);

    return errorMessage;
  },
};

const errorHandlers: ErrorHandler[] = [
  firebaseErrorHandler,
  apiFetcherErrorHandler,
  generalErrorHandler,
];

export function errorCatcher(error: unknown): string {
  try {
    const handler = errorHandlers.find((h) => h.canHandle(error));
    if (!handler) {
      console.error('No handler found for error:', error);
      return 'An unexpected error occurred';
    }
    return handler.handle(error);
  } catch (e) {
    console.error('Error in errorCatcher:', e);
    return 'An unexpected error occurred';
  }
}
